import React from 'react';
import { Website, type components } from '~/fecom-api/schema';
import { cn } from '~/lib/utils';
interface StockStatusProps {
  classname?: string;
  stockInfo?: components['schemas']['StockInfo'];
  isDirectDelivery?: boolean;
  directDeliveryText?: string;
  variant?: 'normal' | 'small';
  isThirdPartyDDelivery?: boolean;
  isRestricted?: boolean;
  inStockDeliveryMessage?: string;
  websiteId: Website;
}

type StockStatusText =
  | 'In stock'
  | 'Out of stock'
  | 'Direct Delivery'
  | 'Preorder'
  | null;

const StockStatus: React.FC<StockStatusProps> = ({
  classname,
  stockInfo,
  isDirectDelivery,
  directDeliveryText,
  variant,
  isThirdPartyDDelivery = false,
  isRestricted = false,
  inStockDeliveryMessage,
  websiteId,
}) => {
  if (
    websiteId === Website.FindelInternational ||
    websiteId === Website.InternationalMarketplace
  ) {
    return null;
  }

  if (!stockInfo?.isStockRelevant) {
    return null;
  }

  function getStockStatus(): StockStatusText {
    if (isDirectDelivery) {
      return 'Direct Delivery';
    }

    if (stockInfo?.isPreOrder) {
      return 'Preorder';
    }

    if (stockInfo?.stockLevel === 0) {
      return 'Out of stock';
    }

    if (stockInfo?.stockLevel && stockInfo.stockLevel > 0) {
      return 'In stock';
    }

    return null;
  }

  const getStockMessage = (stockStatus: StockStatusText | null) => {
    if (variant === 'small') {
      return stockStatus;
    }

    if (isDirectDelivery && directDeliveryText) {
      return directDeliveryText;
    }

    if (stockInfo.expectedBackInStockMessage) {
      return stockStatus + ' - ' + stockInfo.expectedBackInStockMessage;
    }

    if (inStockDeliveryMessage && !isRestricted && !isThirdPartyDDelivery) {
      return stockStatus + ' - ' + inStockDeliveryMessage;
    }

    if (stockInfo.thirdPartyDirectDeliveryMessage) {
      return stockStatus + ' - ' + stockInfo.thirdPartyDirectDeliveryMessage;
    }
    if (stockStatus === 'Preorder' && stockInfo.preOrderDate) {
      const date = new Date(stockInfo.preOrderDate);

      return stockStatus + ' - Available from ' + date.toLocaleDateString();
    }

    return stockStatus;
  };

  const status = getStockStatus();

  const stockMessage = getStockMessage(status);

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        { 'text-sm leading-normal text-[#626262]': variant === 'small' },
        {
          'items-start sm:items-center':
            (inStockDeliveryMessage &&
              !isRestricted &&
              !isThirdPartyDDelivery) ??
            (isDirectDelivery && directDeliveryText),
        },
        classname,
      )}
      data-testid="stock-status"
    >
      <span
        className={cn(
          'block size-4 shrink-0 rounded-full',
          {
            'bg-[var(--stock-level-low)]': status === 'Out of stock',
            'bg-[var(--stock-level-medium)]': status === 'Preorder',
            'bg-[var(--stock-level-high)]':
              status === 'In stock' || status === 'Direct Delivery',
            'size-[0.875rem]': variant === 'small',
          },
          {
            'mt-1 sm:mt-0':
              (inStockDeliveryMessage &&
                !isRestricted &&
                !isThirdPartyDDelivery) ??
              (isDirectDelivery && directDeliveryText),
          },
        )}
      ></span>
      <p>{stockMessage}</p>
    </div>
  );
};

export default StockStatus;
